<template>
  <form novalidate test-id="form" @submit.prevent="submit">
    <LsModal title track-id="review_lead_modal">
      <template #body>
        <div class="review-lead-modal--heading-upper">
          <LsIcon
            v-if="screenSize.desktop"
            type="checkmark"
            size="lg"
            class="review-lead-modal--icon"
          />
          <h1 class="review-lead-modal--title">{{ modalTitle }}</h1>
        </div>
        <p class="review-lead-modal--date">{{ customerLead.created_at | date }}</p>
        <div class="review-lead-modal--star-div">
          <p class="review-lead-modal--please-rate">Please rate {{ firstName }}'s service</p>
          <LsStars v-model="rating" track-id="review_lead_modal-rating" interactive size="xxlg" />
        </div>
        <BCollapse :open="!!rating">
          <LsInput
            v-model="notes"
            track-id="review_lead_modal-notes"
            type="textarea"
            name="review_lead_modal-notes"
            :label="getInputLabel"
          />
        </BCollapse>
      </template>

      <template #footer>
        <div class="review-lead-modal--buttons">
          <LsButton
            track-id="review_lead_modal-remind"
            text="Remind me in 14 days"
            bg-color="primary-light"
            text-color="primary"
            :loading="customerLeads.loadingStatus.isUpdating && buttonLoading === 'remind'"
            :disabled="customerLeads.loadingStatus.isUpdating"
            @click="remindMe"
          />
          <LsButton
            v-if="rating"
            class="review-lead-modal--submit-button"
            track-id="review_lead_modal-submit"
            text="Submit Rating"
            :loading="customerLeads.loadingStatus.isUpdating && buttonLoading === 'submit'"
            :disabled="customerLeads.loadingStatus.isUpdating"
            type="submit"
          />
        </div>
      </template>
    </LsModal>
  </form>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import { LsModal, LsStars, LsIcon, LsInput, LsButton } from '@/components/common';
import { CUSTOMER_LEADS__REVIEW_REMIND_ME, CUSTOMER_LEADS__SUBMIT_REVIEW } from '@/store/types';
import VueTypes from 'vue-types';
import _ from 'lodash';

export default {
  name: 'ReviewLeadModal',
  components: { LsModal, LsStars, LsInput, LsIcon, LsButton },
  props: {
    customerLead: VueTypes.shape({
      id: VueTypes.number.isRequired,
      created_at: VueTypes.string.isRequired,
      services: VueTypes.arrayOf(
        VueTypes.shape({
          name: VueTypes.string.isRequired,
        }).loose.isRequired
      ).isRequired,
    }).isRequired.loose,
    firstName: VueTypes.string.isRequired,
    propertyId: VueTypes.number.isRequired,
  },
  data() {
    return {
      // form
      rating: 0,
      notes: '',

      buttonLoading: null,
    };
  },
  computed: {
    ...mapState(['customerLeads', 'screenSize']),

    modalTitle() {
      const { services } = this.customerLead;
      const servicesLength = _.size(services);
      const firstService = _.get(services, '[0].name');

      return `Your project for ${firstService} ${servicesLength > 1 ? '+ more ' : ''}is complete!`;
    },

    getInputLabel() {
      return this.rating === 5
        ? `Leave an epic 250 character review and we’ll feature ${this.firstName} on our site!`
        : 'Additional comments?';
    },
  },
  methods: {
    ...mapActions([CUSTOMER_LEADS__REVIEW_REMIND_ME, CUSTOMER_LEADS__SUBMIT_REVIEW]),

    async remindMe() {
      this.buttonLoading = 'remind';

      const { customerLead, propertyId } = this;

      await this[CUSTOMER_LEADS__REVIEW_REMIND_ME]({
        customerLeadId: customerLead.id,
        propertyId,
        onSuccess: this.close,
      });
    },

    async submit() {
      this.buttonLoading = 'submit';

      const { notes, rating, customerLead, propertyId } = this;

      await this[CUSTOMER_LEADS__SUBMIT_REVIEW]({
        notes,
        rating,
        customerLeadId: customerLead.id,
        propertyId,
        onSuccess: this.close,
      });
    },

    close() {
      this.$parent.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.review-lead-modal--heading-upper {
  display: flex;

  @media (max-width: $mobile-max-width) {
    text-align: center;
    justify-content: center;
  }
}

.review-lead-modal--icon {
  transform: translateY(4px);
}

.review-lead-modal--title {
  margin-left: 10px;

  @media (max-width: $mobile-max-width) {
    margin-left: 0;
  }
}

.review-lead-modal--date {
  padding: 10px 0;
  font-size: $size-3;
  font-weight: bold;

  @media (max-width: $mobile-max-width) {
    text-align: center;
  }
}

.review-lead-modal--please-rate {
  color: $primary;
  font-weight: bold;

  @media (max-width: $mobile-max-width) {
    margin: 8px 0;
  }
}

.review-lead-modal--star-div {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0px;
  border-top: 1px solid $primary-light;
  border-bottom: 1px solid $primary-light;

  @media (max-width: $mobile-max-width) {
    flex-direction: column;
  }
}

.review-lead-modal--buttons {
  @media (max-width: $mobile-max-width) {
    width: 100%;
  }
}

.review-lead-modal--submit-button {
  margin-left: 10px;

  @media (max-width: $mobile-max-width) {
    margin-left: 0px;
    margin-top: 10px;
  }
}

.themed-app {
  .review-lead-modal--date {
    font-size: $theme-txt-title-sm;
  }
  .review-lead-modal--please-rate {
    color: $theme-color-primary;
  }

  .review-lead-modal--star-div {
    border-color: 1px solid $theme-color-primary-light;
  }
}
</style>
